/**
 * The deadline line's five states (R2) and the "Sun 1:00 PM" time format, as
 * pure functions taking `nowMs` as a parameter so they are testable without
 * faking a clock. `packages/errors`' `formatTime` renders `"2:00 PM"` with no
 * weekday, so this formatter fills that gap.
 */
import { PICK_DEADLINE_LOCKED_NOTE } from "./pickCopy";

export type typeDeadlineLine =
  | { kind: "locked"; note: string }
  | { kind: "plain"; timeLabel: string }
  | { kind: "countdown"; timeLabel: string; countdownLabel: string }
  | { kind: "warning"; timeLabel: string; countdownLabel: string };

export function formatKickoffLabel(input: {
  isoInstant: string;
  timeZone: string;
}): string {
  const date = new Date(input.isoInstant);
  if (Number.isNaN(date.getTime())) return "";
  const formatted = new Intl.DateTimeFormat("en-US", {
    timeZone: input.timeZone,
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
  return formatted.replace(",", "").replace(/\s+/g, " ").trim();
}

export function formatCountdownLabel(remainingMs: number): string {
  if (remainingMs < 60_000) return "under a minute";
  if (remainingMs < 3_600_000) {
    const minutes = Math.floor(remainingMs / 60_000);
    return `${minutes}m`;
  }
  const hours = Math.floor(remainingMs / 3_600_000);
  const minutes = Math.floor((remainingMs % 3_600_000) / 60_000);
  return `${hours}h ${minutes}m`;
}

export function resolveDeadlineLine(input: {
  locksAtIso: string;
  nowMs: number;
  timeZone: string;
  isRoundLocked: boolean;
}): typeDeadlineLine {
  if (input.isRoundLocked) {
    return { kind: "locked", note: PICK_DEADLINE_LOCKED_NOTE };
  }
  const locksAtMs = new Date(input.locksAtIso).getTime();
  const remainingMs = locksAtMs - input.nowMs;
  if (remainingMs <= 0) {
    return { kind: "locked", note: PICK_DEADLINE_LOCKED_NOTE };
  }
  const timeLabel = formatKickoffLabel({
    isoInstant: input.locksAtIso,
    timeZone: input.timeZone,
  });
  if (remainingMs < 3_600_000) {
    return {
      kind: "warning",
      timeLabel,
      countdownLabel: formatCountdownLabel(remainingMs),
    };
  }
  if (remainingMs < 86_400_000) {
    return {
      kind: "countdown",
      timeLabel,
      countdownLabel: formatCountdownLabel(remainingMs),
    };
  }
  return { kind: "plain", timeLabel };
}
